import * as React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

import { layout, type AppPageWidth } from "./tokens";

export type PublicNavItem = {
  label: string;
  href: string;
  external?: boolean;
};

export type PublicFooterNavColumn = {
  items: PublicNavItem[];
};

export const publicPageNavItems: PublicNavItem[] = [
  { label: "Startseite", href: "/" },
];

export const publicFooterNavColumns: PublicFooterNavColumn[] = [
  {
    items: [{ label: "Impressum", href: "/impressum" }],
  },
  {
    items: [
      { label: "Datenschutz", href: "/datenschutz" },
      { label: "Datenschutz App", href: "/datenschutz-app" },
    ],
  },
  {
    items: [
      {
        label: "Kontakt",
        href: "mailto:kontakt@logami.de",
        external: true,
      },
    ],
  },
];

type AppPublicLayoutProps = {
  children: React.ReactNode;
  navItems?: PublicNavItem[];
  footerNavColumns?: PublicFooterNavColumn[];
  cta?: PublicNavItem;
};

export function AppPublicLayout({
  children,
  footerNavColumns = publicFooterNavColumns,
  cta,
}: AppPublicLayoutProps) {
  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      <SkipToContentLink />
      <AppPublicHeader cta={cta} />

      <Box id="main-content" component="main" sx={{ flex: 1 }}>
        {children}
      </Box>

      <AppPublicFooter navColumns={footerNavColumns} />
    </Box>
  );
}

function SkipToContentLink() {
  return (
    <Box
      component="a"
      href="#main-content"
      sx={{
        position: "absolute",
        left: -10000,
        top: 8,
        zIndex: 1500,
        px: 2,
        py: 1,
        borderRadius: 1,
        bgcolor: "background.paper",
        color: "text.primary",
        boxShadow: 3,
        textDecoration: "none",
        "&:focus": {
          left: 8,
        },
      }}
    >
      Zum Inhalt springen
    </Box>
  );
}

type AppPublicHeaderProps = {
  loginHref?: string;
  cta?: PublicNavItem;
  showActions?: boolean;
};

export function AppPublicHeader({
  loginHref = "/login",
  cta = {
    label: "Pilotkunde werden",
    href: "/#early-bird",
  },
  showActions = true,
}: AppPublicHeaderProps) {
  return (
    <Box
      component="header"
      sx={{
        py: 2.5,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: layout.page.maxWidth.content,
          px: layout.page.paddingX,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Link
            href="/"
            underline="none"
            color="text.primary"
            aria-label="Logami Startseite"
            sx={{
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                component="img"
                src="/logami.svg"
                alt=""
                aria-hidden="true"
                sx={{
                  width: 28,
                  height: 28,
                  display: "block",
                }}
              />

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  letterSpacing: -0.3,
                }}
              >
                Logami
              </Typography>
            </Stack>
          </Link>

          {showActions && (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ display: { xs: "none", sm: "flex" } }}
            >
              <Button
                color="inherit"
                href={loginHref}
                sx={{ textTransform: "none" }}
              >
                Anmelden
              </Button>

              <Button
                variant="contained"
                href={cta.href}
                target={cta.external ? "_blank" : undefined}
                rel={cta.external ? "noopener noreferrer" : undefined}
                sx={{
                  textTransform: "none",
                  borderRadius: 1.5,
                }}
              >
                {cta.label}
              </Button>
            </Stack>
          )}
        </Stack>
      </Container>
    </Box>
  );
}

type AppPublicFooterProps = {
  navColumns?: PublicFooterNavColumn[];
};

export function AppPublicFooter({
  navColumns = publicFooterNavColumns,
}: AppPublicFooterProps) {
  return (
    <Box
      component="footer"
      sx={{
        py: { xs: 6, md: 8 },
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: layout.page.maxWidth.content,
          px: layout.page.paddingX,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start" }}
        >
          <Typography variant="body2" color="text.secondary">
            © Logami — Digitale Einsatzdokumentation für mobile Handwerksteams
          </Typography>

          <Stack
            direction="row"
            spacing={{ xs: 1.5, sm: 2 }}
            divider={
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  borderColor: "divider",
                  opacity: 0.7,
                }}
              />
            }
            useFlexGap
            flexWrap="wrap"
            alignItems="flex-start"
            color="text.secondary"
          >
            {navColumns.map((column, columnIndex) => (
              <Stack
                key={columnIndex}
                spacing={0.75}
                sx={{
                  px: { xs: 0.5, sm: 1 },
                  minWidth: "max-content",
                }}
              >
                {column.items.map((item) => (
                  <Link
                    key={`${item.label}-${item.href}`}
                    href={item.href}
                    underline="none"
                    color="inherit"
                    target={item.external ? "_blank" : undefined}
                    rel={item.external ? "noopener noreferrer" : undefined}
                    sx={{
                      typography: "body2",
                      lineHeight: 1.7,
                      "&:hover": {
                        color: "text.primary",
                        textDecoration: "underline",
                        textUnderlineOffset: "3px",
                      },
                    }}
                  >
                    {item.label}
                  </Link>
                ))}
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

type AppPublicPageProps = {
  children: React.ReactNode;
  width?: AppPageWidth;
  sx?: SxProps<Theme>;
};

export function AppPublicPage({
  children,
  width = "content",
  sx,
}: AppPublicPageProps) {
  return (
    <Container
      maxWidth={false}
      sx={{
        maxWidth: layout.page.maxWidth[width],
        px: layout.page.paddingX,
        py: layout.page.paddingY,
        ...sx,
      }}
    >
      {children}
    </Container>
  );
}

type PublicDocumentProps = {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
};

export function PublicDocument({ children, sx }: PublicDocumentProps) {
  return (
    <Card
      elevation={0}
      variant="outlined"
      sx={{
        borderRadius: layout.card.radius,
      }}
    >
      <CardContent sx={{ p: layout.card.padding }}>
        <Box
          component="article"
          sx={{
            "& h1": {
              typography: "h3",
              fontWeight: 850,
              letterSpacing: -0.8,
              mt: 0,
              mb: 1,
            },

            "& .lead": {
              typography: "body1",
              color: "text.secondary",
              lineHeight: 1.75,
              mb: 4,
            },

            "& h2": {
              typography: "h5",
              fontWeight: 800,
              mt: 4,
              mb: 1.25,
            },

            "& h3": {
              typography: "h6",
              fontWeight: 750,
              mt: 3,
              mb: 1,
            },

            "& p, & address": {
              typography: "body1",
              lineHeight: 1.75,
              m: 0,
              mb: 2,
            },

            "& address": {
              fontStyle: "normal",
            },

            "& ul": {
              mt: 0,
              mb: 2,
              pl: 3,
            },

            "& li": {
              typography: "body1",
              lineHeight: 1.7,
              mb: 0.5,
            },

            "& a": {
              color: "primary.main",
              textDecoration: "none",
              fontWeight: 600,
            },

            "& a:hover": {
              textDecoration: "underline",
              textUnderlineOffset: "3px",
            },

            ...sx,
          }}
        >
          {children}
        </Box>
      </CardContent>
    </Card>
  );
}
